import type { DropDownResult, OdinDataFilter, OdinDataRetrievalOptions, OdinKey } from '@myosh/odin-components';
import { isEqual } from 'lodash';
import { useRef } from 'react';
import type { LookupTypeFieldApiProps, PersonOptionApiProps } from '../../../@types/options';
import { useLazyPersonFieldOptionsQuery } from '../../../redux/services/field';
import { isWebView } from '../../../redux/util/webview-base-query';
import { normalizeFieldFilters } from '../../../utils/webview-field-utils';

// A custom hook that provides a trigger to fetch person field options.
const useFetchPersonFieldOptions = (textField: string) => {
  const nextRef = useRef<string | undefined>(undefined);
  const fieldFiltersRef = useRef<Record<string, OdinDataFilter> | undefined>(undefined);
  // Cache WebView results so page>1 requests return the already-fetched data
  // instead of dispatching redundant identical queries.
  const webViewCacheRef = useRef<DropDownResult[] | undefined>(undefined);

  const [getPersonFieldOptions] = useLazyPersonFieldOptionsQuery();

  const fetchPersonFieldOptions = async (id: OdinKey, options: OdinDataRetrievalOptions & LookupTypeFieldApiProps) => {
    const inWebView = isWebView();

    // In WebView mode, return cached results for page>1 since all data was
    // already fetched on page 1 with limit=-1.
    if (inWebView && options.page !== 1 && webViewCacheRef.current) {
      return webViewCacheRef.current;
    }

    const canPerformRequest = id && options && (options.page === 1 || nextRef.current || options.getAllData);

    if (canPerformRequest) {
      const haveFiltersChanged = !isEqual(options?.fieldFilters, fieldFiltersRef.current);
      if (haveFiltersChanged) {
        nextRef.current = undefined;
        webViewCacheRef.current = undefined;
      }
      fieldFiltersRef.current = options?.fieldFilters;

      const baseFilters = inWebView
        ? normalizeFieldFilters(options?.fieldFilters)
        : options?.fieldFilters;
      const filters = baseFilters ? { ...baseFilters } : undefined;
      if (filters?.[textField]) {
        filters.value = filters[textField];
        delete filters[textField];
      }

      const fetchAll = options.getAllData || inWebView;
      const personOptions: PersonOptionApiProps = {
        queryId: Number(id),
        limit: fetchAll ? -1 : (options.pageSize ?? 50),
        filters,
        next: inWebView ? undefined : nextRef.current,
        keyValue: options.keyValue,
        hierarchyFieldId: options.hierarchyFieldId,
      };

      try {
        // prefer cache value to limit amount of requests sent out
        const data = await getPersonFieldOptions(personOptions, true).unwrap();
        // In WebView mode all data is fetched in one request — don't store the cursor
        // to prevent redundant pagination requests
        nextRef.current = inWebView ? undefined : data.next;

        const personFieldOptions: DropDownResult[] | undefined = data.items?.map((item) => {
          return {
            value: item.id,
            text: item.value,
            managerId: item?.managerId,
            managerText: item?.managerValue,
          };
        });

        // Cache results in WebView mode for page>1 short-circuit
        if (inWebView) {
          webViewCacheRef.current = personFieldOptions;
        }

        return personFieldOptions;
      } catch (_) {
        nextRef.current = undefined;
      }
    }
  };

  return fetchPersonFieldOptions;
};

export default useFetchPersonFieldOptions;
